import { useState, useEffect } from "react";
import { Row, Col } from "antd";
import moment from "moment";

import GeneralTable from "../components/Generals/OrdersTable/GeneralTable";
import AgentTable from "../components/Generals/OrdersTable/AgentTable";
import OrderService from "../utils/api/orders";
import UserService from "../utils/api/users";
import LayoutPage from "../layouts/Main";

export default function SeguimientoOrdenes(props) {
  const role = localStorage.getItem("role");
  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user?.id;
  const [openOrders, setOpenOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);

      if (!userId) { 
        setLoading(false);
        return;
      }

      const extractArray = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.data?.orders)) return payload.data.orders;
        if (Array.isArray(payload?.data?.services)) return payload.data.services;
        if (Array.isArray(payload?.orders)) return payload.orders;
        if (Array.isArray(payload?.services)) return payload.services;
        return [];
      };

      const normalizeId = (value) => {
        if (value === undefined || value === null) return null;
        return String(value);
      };

      const getClientIdFromOrder = (order) => {
        return (
          order?.client_id ||
          order?.clientId ||
          order?.buyer_id ||
          order?.buyerId ||
          order?.client?.id ||
          null
        );
      };

      const serviceBelongsToClient = (service, assignedClientIds) => {
        const serviceClientId = normalizeId(
          service?.client_id ||
          service?.clientId ||
          service?.buyer_id ||
          service?.buyerId
        );
        return !!serviceClientId && assignedClientIds.has(serviceClientId);
      };

      let orderServicePromise;

      if (role === '1' || role === '2') {
        orderServicePromise = OrderService.All();
      } 
      else if (role === '3') {
        orderServicePromise = OrderService.All();
      } 
      else if (role === '4') {
        orderServicePromise = OrderService.GetOrdersByUser(userId);
      } 
      else if (role === '6') {
        orderServicePromise = OrderService.All();
      }
      else if (role === '5') {
        orderServicePromise = OrderService.All();
      } 
      else {
        orderServicePromise = Promise.resolve({ data: [] });
      }

      if (!orderServicePromise) {
        setOpenOrders([]);
        setLoading(false);
        return;
      }

      try {
        const response = await orderServicePromise;
        const data = response?.data;
        let resultData = [];
        
        if (Array.isArray(data)) {
          resultData = data;
        } else if (data) {
          resultData = data.orders || data.services || data.proposals || [];
        }

        if ((role === '3' || role === '5')) {
          let assignedClients = [];
          try {
            const assignedClientsResponse = await UserService.GetClients({
              buyerId: role === '3' ? userId : undefined,
              sellerId: role === '5' ? userId : undefined,
            });
            assignedClients = extractArray(assignedClientsResponse);
          } catch (error) {
            assignedClients = [];
          }

          const assignedClientIds = new Set(
            assignedClients
              .map((client) => normalizeId(client?.id || client?.client_id || client?.clientId))
              .filter(Boolean)
          );

          if (assignedClientIds.size > 0) {
            resultData = resultData.filter((order) => {
              const orderClientId = normalizeId(getClientIdFromOrder(order));
              if (orderClientId && assignedClientIds.has(orderClientId)) {
                return true;
              }

              const services = Array.isArray(order?.services) ? order.services : [];
              return services.some((service) => serviceBelongsToClient(service, assignedClientIds));
            });
          } else {
            resultData = [];
          }
        }

        if (role === '6') {
          resultData = resultData
            .map((order) => {
              const services = Array.isArray(order?.services) ? order.services : [];

              // Keep only services relevant for supplier view to reduce processing/render cost.
              const relevantServices = services.filter((service) => {
                if (!service || service.status === 1) return false;

                return (
                  service.status === 2 ||
                  (service.status >= 3 && String(service.supplier_id) === String(userId))
                );
              });

              if (relevantServices.length === 0) {
                return null;
              }

              return {
                ...order,
                services: relevantServices,
              };
            })
            .filter(Boolean);
        }

        // Para COMPRAS (3) y VENTAS (5), agrupar servicios por orden
        if ((role === '3' || role === '5') && resultData.length > 0) {
          // Verificar si ya son órdenes o son servicios sueltos
          const firstItem = resultData[0];
          
          // Si el primer item tiene 'order_id' pero no tiene 'services', son servicios sueltos
          if (firstItem?.order_id && !firstItem?.services) {
            const ordersMap = new Map();
            
            // Paso 1: Construir el mapa de órdenes con la información disponible en servicios
            resultData.forEach(service => {
              const orderId = service.order_id || service.orderId;
              if (!orderId) return;

              const inferredTitle = service.order_title || service.orderTitle || service.order_name || service.orderName || `Orden ${orderId}`;

              if (!ordersMap.has(orderId)) {
                ordersMap.set(orderId, {
                  id: orderId,
                  orderId: orderId,
                  title: inferredTitle,
                  client_bussiness: service.client_bussiness || 'N/A',
                  client_name: service.client_name || 'N/A',
                  created_at: service.order_created_at || service.created_at,
                  updated_at: service.updated_at || service.updatedAt,
                  services: []
                });
              }

              const order = ordersMap.get(orderId);

              if ((!order.title || order.title === `Orden ${orderId}`) && inferredTitle) {
                order.title = inferredTitle;
              }

              order.services.push(service);
              
              const serviceDate = moment(service.updated_at || service.updatedAt).valueOf();
              const orderDate = moment(order.updated_at).valueOf();
              if (serviceDate > orderDate) {
                order.updated_at = service.updated_at || service.updatedAt;
              }
            });
            resultData = Array.from(ordersMap.values());
          }
        }

        setOpenOrders(resultData);
      } catch (error) {
        console.error(`Error fetching orders for role ${role}:`, error);
        setOpenOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [role, userId]);

  const isAgentView = role === '2'; // Solo FINANZAS usa AgentTable

  return (
    <LayoutPage>
      <Row gutter={24}>
        <Col xs={24}>
          {isAgentView ? (
            <AgentTable openOrders={openOrders} loading={loading} />
          ) : (
            <GeneralTable openOrders={openOrders} loading={loading} />
          )}
        </Col>
      </Row>
    </LayoutPage>
  );
}